import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import './Header.css';

const Header = ({ session, profile, onLogout, currentTheme, toggleTheme }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProductDropdownOpen, setIsProductDropdownOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();

  // Close menus on route change
  useEffect(() => {
      setIsMobileMenuOpen(false);
      setIsProductDropdownOpen(false);
      setIsUserMenuOpen(false);
  }, [location]);

  const handleLogout = async () => {
    await onLogout();
    navigate('/login');
  };

  // --- 1. USER & TIER LOGIC ---
  const userName = profile?.full_name || 'User';
  const userEmail = session?.user?.email || '';
  const userInitials = userName.charAt(0).toUpperCase();
  
  const tier = (profile?.subscription_tier || 'starter').toLowerCase();
  
  // Starter Calcs
  const starterLimit = 3;
  const dailyUsed = profile?.daily_usage_count || 0;
  const starterRemaining = Math.max(0, starterLimit - dailyUsed);
  const starterPercent = (starterRemaining / starterLimit) * 100;

  // Lite Calcs
  const creditsBalance = profile?.credits_balance || 0;
  const liteBase = 150; 
  const litePercent = Math.min(100, (creditsBalance / liteBase) * 100);

  // Helper: Progress Color
  const getProgressColor = (percent) => {
    if (percent > 50) return '#10B981'; // Green
    if (percent > 20) return '#F59E0B'; // Yellow
    return '#EF4444'; // Red
  };

  // Helper: Render Credit Widget
  const renderCreditWidget = (isMobile = false) => {
    const isUnlimited = tier === 'pro' || tier === 'agency';
    const isLite = tier === 'lite';
    
    const percent = isLite ? litePercent : starterPercent;
    const color = getProgressColor(percent);
    const label = isLite ? 'Monthly Credits' : 'Daily Free Limit';
    const valueText = isLite ? `${creditsBalance} Left` : `${starterRemaining}/3 Left`;

    if (isUnlimited) {
        return (
            <div className={isMobile ? "mobile-credit-box" : "credit-widget-mini"}>
                <div className="credit-row" style={{justifyContent: 'center', marginBottom: '5px'}}>
                    <span style={{color: '#8A181A', fontWeight: '800', fontSize: '1.2rem'}}>∞</span>
                </div>
                <div className="credit-row" style={{justifyContent: 'center'}}>
                    <span style={{fontWeight: '700', color: 'var(--text-dark)'}}>Unlimited Access</span>
                </div>
                <Link to="/account/usage" className="buy-credits-link" style={{textAlign: 'center', display: 'block'}}>
                    View Plan
                </Link>
            </div>
        );
    }

    return (
        <div className={isMobile ? "mobile-credit-box" : "credit-widget-mini"}>
            <div className="credit-row">
                <span>{label}</span>
                <span className="credit-count" style={{color: color}}>{valueText}</span>
            </div>
            <div className="credit-bar-bg">
                <div className="credit-bar-fill" style={{width: `${percent}%`, backgroundColor: color}}></div>
            </div>
            <Link to="/account/usage" className="buy-credits-link">
                {tier === 'starter' ? 'Upgrade to Lite' : 'Manage Plan'}
            </Link>
        </div>
    );
  };

  return (
    <>
      <header className="header-container">
        
        {/* 1. BRAND */}
        <Link className="brand-link" to="/">
          <img src="/logo_fullmockup.png" alt="FullMockup" className="brand-logo" />
          <span className="brand-text">FullMockup</span>
        </Link>
        
        {/* 2. DESKTOP NAVIGATION */}
        <nav className="desktop-nav">
            
            {/* Mockup Creator Dropdown */}
            <div className="dropdown-wrapper"
                 onMouseEnter={() => setIsProductDropdownOpen(true)}
                 onMouseLeave={() => setIsProductDropdownOpen(false)}>
                
                <button className={`nav-link ${isProductDropdownOpen ? 'active' : ''}`}>
                    {/* Added Icon here as requested */}
                    <i className="bi bi-magic mr-1" style={{fontSize: '1rem'}}></i>
                    Mockup Creator 
                    <i className={`bi bi-chevron-down small-icon ${isProductDropdownOpen ? 'rotate' : ''}`}></i>
                </button>
                
                {isProductDropdownOpen && (
                    <div className="dropdown-menu">
                        <Link to="/apparel" className="dropdown-item">
                            <span className="dropdown-title"><i className="bi bi-bag-fill"></i> Apparel Generator</span>
                            <span className="dropdown-desc">T-Shirts, Hoodies, Oversized.</span>
                        </Link>
                        <Link to="/home-living" className="dropdown-item">
                            <span className="dropdown-title"><i className="bi bi-cup-hot-fill"></i> Home & Living</span>
                            <span className="dropdown-desc">Mugs, Wall Art, Canvas.</span>
                        </Link>
                        <Link to="/tech-social" className="dropdown-item">
                            <span className="dropdown-title"><i className="bi bi-phone-fill"></i> Tech & Social</span>
                            <span className="dropdown-desc">Phone cases, Social posts.</span>
                        </Link>
                    </div>
                )}
            </div>
            
            <Link to="/tool" className="nav-link">Tools</Link>
            <Link to="/pricing" className="nav-link">Pricing</Link>
        </nav>

        {/* 3. ACTIONS (Right Side) */}
        <div className="header-actions">
            
            {/* Theme Toggle */}
            <button onClick={toggleTheme} className="theme-toggle-btn tablet-visible" title="Toggle Theme">
                <i className={`bi ${currentTheme === 'dark' ? 'bi-sun-fill' : 'bi-moon-stars-fill'}`}></i>
            </button>

            {/* --- AUTH STATE SWITCH --- */}
            {session ? (
                /* LOGGED IN: AVATAR COMMAND CENTER */
                <div className="user-menu-wrapper" 
                     onMouseEnter={() => setIsUserMenuOpen(true)}
                     onMouseLeave={() => setIsUserMenuOpen(false)}>
                    
                    <button className="avatar-btn">
                        {/* Sync: Shows Image if available, else Initials */}
                        {profile?.avatar_url ? (
                            <img src={profile.avatar_url} alt="User Avatar" />
                        ) : (
                            <div className="avatar-initials">{userInitials}</div>
                        )}
                    </button>

                    {isUserMenuOpen && (
                        <div className="user-dropdown-panel">
                            {/* Identity */}
                            <div className="user-identity">
                                <p className="user-name">{userName}</p>
                                <p className="user-email">{userEmail}</p>
                            </div>

                            {/* Credit Tracker */}
                            {renderCreditWidget(false)}

                            {/* Account Links (Dashboard Removed) */}
                            <div className="user-nav-list">
                                <Link to="/account/profile" className="user-nav-item">
                                    <i className="bi bi-person-fill"></i> Account Settings
                                </Link>
                                <Link to="/account/gallery" className="user-nav-item">
                                    <i className="bi bi-images"></i> My Gallery
                                </Link>
                                <Link to="/account/billing" className="user-nav-item">
                                    <i className="bi bi-credit-card-fill"></i> Billing
                                </Link>
                            </div>

                            {/* Logout */}
                            <div className="user-logout-section">
                                <button onClick={handleLogout} className="logout-btn">
                                    <i className="bi bi-box-arrow-right"></i> Sign out
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                /* LOGGED OUT: BUTTON */
                <Link to="/login" className="btn-try">
                    Try v25 <i className="bi bi-arrow-right"></i>
                </Link>
            )}

            {/* Mobile Toggle */}
            <button className="mobile-toggle" onClick={() => setIsMobileMenuOpen(true)}>
                <i className="bi bi-list"></i>
            </button>
        </div>
      </header>

      {/* 4. MOBILE SIDE DRAWER */}
      <div className={`mobile-drawer-backdrop ${isMobileMenuOpen ? 'open' : ''}`} 
           onClick={() => setIsMobileMenuOpen(false)}>
      </div>

      <aside className={`mobile-drawer ${isMobileMenuOpen ? 'open' : ''}`}>
          <div className="drawer-header">
              <span className="brand-text">FullMockup</span>
              <button className="drawer-close" onClick={() => setIsMobileMenuOpen(false)}>
                  <i className="bi bi-x-lg"></i>
              </button>
          </div>

          <div className="drawer-content">
              {/* Mobile Profile Card */}
              {session && (
                  <div className="mobile-user-card">
                      <div className="mobile-user-info">
                          <div className="avatar-initials small">
                              {profile?.avatar_url ? (
                                  <img src={profile.avatar_url} alt="Avatar" style={{width:'100%', height:'100%', borderRadius:'50%', objectFit:'cover'}} />
                              ) : (
                                  userInitials
                              )}
                          </div>
                          <div>
                              <p className="user-name">{userName}</p>
                              <p className="user-email">{userEmail}</p>
                          </div>
                      </div>
                      
                      {renderCreditWidget(true)}
                  </div>
              )}

              {/* Links */}
              <nav className="drawer-nav">
                  <div className="drawer-label">Products</div>
                  <Link to="/apparel" className="drawer-link"><i className="bi bi-bag"></i> Apparel Engine</Link>
                  <Link to="/home-living" className="drawer-link"><i className="bi bi-cup-hot"></i> Home & Living</Link>
                  <Link to="/tech-social" className="drawer-link"><i className="bi bi-phone"></i> Tech & Social</Link>
                  
                  <div className="drawer-divider"></div>
                  
                  <Link to="/tool" className="drawer-link"><i className="bi bi-grid"></i> All Tools</Link>
                  <Link to="/pricing" className="drawer-link"><i className="bi bi-tag"></i> Pricing</Link>

                  {session && (
                      <>
                        <div className="drawer-divider"></div>
                        <div className="drawer-label">Account</div>
                        {/* Dashboard Link Removed here too */}
                        <Link to="/account/profile" className="drawer-link"><i className="bi bi-person"></i> Account</Link>
                        <Link to="/account/gallery" className="drawer-link"><i className="bi bi-images"></i> Gallery</Link>
                        <Link to="/account/billing" className="drawer-link"><i className="bi bi-credit-card"></i> Billing</Link>
                      </>
                  )}
              </nav>
          </div>

          <div className="drawer-footer">
              <button onClick={toggleTheme} className="drawer-theme-toggle">
                  <span>Theme</span>
                  <i className={`bi ${currentTheme === 'dark' ? 'bi-sun-fill' : 'bi-moon-stars-fill'}`}></i>
              </button>
              
              {session ? (
                  <button onClick={handleLogout} className="drawer-logout">Sign Out</button>
              ) : (
                  <Link to="/login" className="drawer-login-btn">Log In / Sign Up</Link>
              )}
          </div>
      </aside>
    </>
  );
};

export default Header;